import React, { Component } from 'react';
import {
  Button,
  Tag,
  Alert,
  Row,
  Col,
  Input,
  InputNumber,
  Steps,
  Select,
  Switch,
  Modal,
  Checkbox,
  message,
} from 'antd';
import {
  verifyDoctorCenterMapping,
  bulkMapDoctorPracticeCenters,
} from '../../services';

const { TextArea } = Input;
const { Step } = Steps;
const { Option } = Select;

const defaultPractice = () => ({
  active: true,
  opdCharges: 0,
  onlineOpdCharges: 0,
  offlineDiscount: 0,
  offlineDiscountOperation: 'percentage',
  onlineDiscount: 0,
  onlineDiscountOperation: 'percentage',
  fDiscount: 0,
  fDiscountOperation: 'percentage',
  fDays: 0,
  onlineFDiscount: 0,
  onlineFDiscountOperation: 'percentage',
  onlineFDays: 0,
  payoutOfflineDiscount: 0,
  visitCommissionPercent: 0,
  offlinePayoutPercent: 100,
  enableVisitCommission: false,
  fulfilmentType: 'center',
  status: 'pending',
  telehealth: false,
  onlineRetailPayout: 0,
  onlineCorporatePayout: 0,
  priceChangeReason: undefined,
  priceChangeReasonOther: '',
});

const extractApiErrorMessage = (err, fallback) => {
  const data = err && err.response && err.response.data;
  if (data && data.err && typeof data.err === 'object') {
    const firstKey = Object.keys(data.err)[0];
    const fieldErr = firstKey ? data.err[firstKey] : null;
    if (fieldErr && fieldErr.msg) return fieldErr.msg;
  }
  if (data && data.message && data.message !== 'Invalid fields') return data.message;
  return fallback;
};

const REASON_OPTIONS = [
  'Fee got revised',
  'Incorrect fee was updated in our system',
  'Incorrect price was informed by the centre while booking the appointment',
  'Other',
];

const PURPLE = '#7651da';

const sectionStyle = {
  background: '#ffffff',
  border: '1px solid #e7e7e7',
  borderRadius: 6,
  padding: 18,
  marginBottom: 16,
  boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
};

const sectionTitleStyle = {
  fontSize: 15,
  fontWeight: 600,
  color: '#514f54',
  marginBottom: 4,
};

const sectionHelpStyle = {
  fontSize: 12,
  color: '#7c7c7c',
  marginBottom: 14,
};

const scrollListStyle = {
  maxHeight: 360,
  overflowY: 'auto',
  paddingRight: 6,
  border: '1px solid #f0f0f0',
  borderRadius: 4,
  padding: 8,
  background: '#fafafa',
};

class DoctorPracticeBulkAdd extends Component {
  constructor(props) {
    super(props);
    this.state = {
      idsInput: '',
      parsedIds: [],
      verifying: false,
      verifyResults: null,
      submitting: false,
      submitResult: null,
      practiceModalVisible: false,
      practice: defaultPractice(),
    };
  }

  reset = () => {
    this.setState({
      idsInput: '',
      parsedIds: [],
      verifying: false,
      verifyResults: null,
      submitting: false,
      submitResult: null,
      practiceModalVisible: false,
      practice: defaultPractice(),
    });
  };

  setPractice = (patch) => {
    this.setState((prev) => ({ practice: Object.assign({}, prev.practice, patch) }));
  };

  onEnableVisitCommissionChange = (e) => {
    const checked = e.target.checked;
    const visitCommissionPercent = checked ? 70 : 0;
    const offlinePayoutPercent = checked ? 30 : 100;
    const newOfflineDiscount = Math.round((this.state.practice.payoutOfflineDiscount || 0) * (offlinePayoutPercent / 100) * 100) / 100;
    this.setPractice({ enableVisitCommission: checked, visitCommissionPercent, offlinePayoutPercent, offlineDiscount: newOfflineDiscount });
  };

  onVisitCommissionPercentChange = (e) => {
    const val = Math.min(100, Math.max(0, Number(e.target.value) || 0));
    const offlinePayoutPercent = 100 - val;
    const newOfflineDiscount = Math.round((this.state.practice.payoutOfflineDiscount || 0) * (offlinePayoutPercent / 100) * 100) / 100;
    this.setPractice({ visitCommissionPercent: val, offlinePayoutPercent, offlineDiscount: newOfflineDiscount });
  };

  openPracticeModal = () => {
    this.setState({ practiceModalVisible: true });
  };

  closePracticeModal = () => {
    if (this.state.submitting) return;
    this.setState({ practiceModalVisible: false });
  };

  parseIds = (raw) => {
    const seen = new Set();
    return raw
      .split(/[,\s]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => Number(s))
      .filter((n) => Number.isInteger(n) && n > 0)
      .filter((n) => {
        if (seen.has(n)) return false;
        seen.add(n);
        return true;
      });
  };

  onIdsInputChange = (e) => {
    const value = e.target.value;
    this.setState({
      idsInput: value,
      parsedIds: this.parseIds(value),
      verifyResults: null,
      submitResult: null,
    });
  };

  removeParsedId = (id) => {
    this.setState((prev) => {
      const remaining = prev.parsedIds.filter((n) => n !== id);
      return {
        parsedIds: remaining,
        idsInput: remaining.join(', '),
        verifyResults: null,
        submitResult: null,
      };
    });
  };

  verify = () => {
    const { parsedIds } = this.state;
    if (parsedIds.length === 0) {
      message.warning('Enter at least one center ID.');
      return;
    }
    this.setState({ verifying: true, verifyResults: null, submitResult: null });
    verifyDoctorCenterMapping(this.props.doctorId, parsedIds)
      .then((res) => {
        this.setState({ verifyResults: res.results || [] });
      })
      .catch(() => {
        message.error('Verification failed. Try again.');
      })
      .finally(() => this.setState({ verifying: false }));
  };

  submit = () => {
    const { verifyResults, practice } = this.state;
    const toMap = (verifyResults || [])
      .filter((r) => !r.isMapped && !r.error)
      .map((r) => r.centerId);
    if (toMap.length === 0) {
      message.warning('No new centers to map.');
      return;
    }
    if (Number(practice.fDiscount) > 0 && Number(practice.fDays) <= 0) {
      message.warning('Follow-up days is required when follow-up discount is set.');
      return;
    }
    if (Number(practice.onlineFDiscount) > 0 && Number(practice.onlineFDays) <= 0) {
      message.warning(
        'Online follow-up days is required when online follow-up discount is set.'
      );
      return;
    }
    if (
      practice.offlineDiscountOperation === 'percentage' &&
      (Number(practice.offlineDiscount) || 0) > 100
    ) {
      message.error('Offline Discount cannot be greater than 100%');
      return;
    }
    if (
      practice.offlineDiscountOperation === 'percentage' &&
      (Number(practice.payoutOfflineDiscount) || 0) > 100
    ) {
      message.error('Visit Payout Discount cannot be greater than 100%');
      return;
    }
    if (
      (Number(practice.offlineDiscount) || 0) >
      (Number(practice.payoutOfflineDiscount) || 0)
    ) {
      message.error('Offline Discount cannot be greater than Visit Payout Discount');
      return;
    }
    if (practice.offlineDiscountOperation === 'fixed' && Number(practice.payoutOfflineDiscount) > Number(practice.opdCharges)) {
      message.error('Visit Payout Discount (fixed) cannot be greater than doctor charges');
      return;
    }
    if (practice.fDiscountOperation === 'fixed' && Number(practice.fDiscount) > Number(practice.opdCharges)) {
      message.error('Followup Discount (fixed) cannot be greater than doctor charges');
      return;
    }
    if (practice.onlineDiscountOperation === 'fixed' && Number(practice.onlineDiscount) > Number(practice.onlineOpdCharges)) {
      message.error('Online Discount (fixed) cannot be greater than online doctor charges');
      return;
    }
    if (practice.onlineFDiscountOperation === 'fixed' && Number(practice.onlineFDiscount) > Number(practice.onlineOpdCharges)) {
      message.error('Online Followup Discount (fixed) cannot be greater than online doctor charges');
      return;
    }
    if (!practice.priceChangeReason) {
      message.warning('Please pick a reason for the change.');
      return;
    }
    if (
      practice.priceChangeReason === 'Other' &&
      !practice.priceChangeReasonOther.trim()
    ) {
      message.warning('Please describe the reason.');
      return;
    }
    const resolvedReason =
      practice.priceChangeReason === 'Other'
        ? practice.priceChangeReasonOther.trim()
        : practice.priceChangeReason;
    const practicePayload = Object.assign({}, practice, {
      priceChangeReason: resolvedReason,
    });
    delete practicePayload.priceChangeReasonOther;
    this.setState({ submitting: true, submitResult: null });
    bulkMapDoctorPracticeCenters(this.props.doctorId, toMap, practicePayload, true)
      .then((res) => {
        this.setState({ submitResult: res, practiceModalVisible: false });
        if (this.props.onSuccess) this.props.onSuccess(res);
      })
      .catch((err) => {
        message.error(extractApiErrorMessage(err, 'Bulk mapping failed.'));
      })
      .finally(() => this.setState({ submitting: false }));
  };

  currentStep = () => {
    const { verifyResults, submitResult } = this.state;
    if (submitResult) return 3;
    if (verifyResults) {
      const toMap = verifyResults.filter((r) => !r.isMapped && !r.error).length;
      return toMap > 0 ? 2 : 1;
    }
    return 0;
  };

  renderEnterSection() {
    const { idsInput, parsedIds, verifying } = this.state;
    return (
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>1. Enter center IDs</div>
        <div style={sectionHelpStyle}>
          Paste comma-separated center IDs (e.g. <code>123, 456, 789</code>). Spaces and
          new lines are also accepted. Duplicates and non-numeric values are ignored.
        </div>
        <TextArea
          rows={3}
          value={idsInput}
          onChange={this.onIdsInputChange}
          placeholder="123, 456, 789"
          style={{ fontFamily: 'monospace' }}
        />
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, color: '#7c7c7c', marginBottom: 6 }}>
            {parsedIds.length} valid ID{parsedIds.length === 1 ? '' : 's'} parsed
          </div>
          <div>
            {parsedIds.map((id) => (
              <Tag
                key={id}
                closable
                onClose={() => this.removeParsedId(id)}
                style={{ marginBottom: 6 }}
              >
                #{id}
              </Tag>
            ))}
          </div>
        </div>
        <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
          <Button
            type="primary"
            loading={verifying}
            disabled={parsedIds.length === 0}
            onClick={this.verify}
            style={{ background: PURPLE, borderColor: PURPLE }}
          >
            Verify
          </Button>
          {parsedIds.length > 0 && <Button onClick={this.reset}>Reset</Button>}
        </div>
      </div>
    );
  }

  renderVerifySection() {
    const { verifyResults, submitting } = this.state;
    if (!verifyResults) return null;
    const alreadyMapped = verifyResults.filter((r) => r.isMapped);
    const toMap = verifyResults.filter((r) => !r.isMapped && !r.error);
    const failed = verifyResults.filter((r) => !r.isMapped && r.error);
    return (
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>2. Verification result</div>
        <div style={sectionHelpStyle}>
          Already-mapped centers are skipped. Unmapped centers will be added as practice
          centers for this doctor on submit. Invalid IDs are reported but not changed.
        </div>
        {toMap.length === 0 && (
          <Alert
            type={alreadyMapped.length > 0 ? 'info' : 'error'}
            showIcon
            message={
              alreadyMapped.length > 0
                ? 'All entered centers are already mapped to this doctor — nothing to add.'
                : 'No valid centers to add.'
            }
            style={{ marginBottom: 16 }}
          />
        )}
        <Row gutter={16}>
          <Col span={8}>
            <div style={{ fontWeight: 600, marginBottom: 8, color: '#7651da' }}>
              Will be added ({toMap.length})
            </div>
            <div style={scrollListStyle}>
              {toMap.length === 0 ? (
                <div style={{ color: '#9b9b9b', fontSize: 13 }}>None</div>
              ) : (
                toMap.map((r) => (
                  <div
                    key={r.centerId}
                    style={{
                      padding: '8px 10px',
                      border: '1px solid #d9c8ff',
                      background: '#f6f3ff',
                      borderRadius: 4,
                      marginBottom: 6,
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>
                      {r.centerName || `Center #${r.centerId}`}{' '}
                      <span style={{ color: '#9b9b9b', fontWeight: 400, fontSize: 12 }}>
                        #{r.centerId}
                      </span>
                    </div>
                    {r.locality && (
                      <div style={{ fontSize: 12, color: '#7c7c7c' }}>{r.locality}</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </Col>
          <Col span={8}>
            <div style={{ fontWeight: 600, marginBottom: 8, color: '#389e0d' }}>
              Already mapped ({alreadyMapped.length})
            </div>
            <div style={scrollListStyle}>
              {alreadyMapped.length === 0 ? (
                <div style={{ color: '#9b9b9b', fontSize: 13 }}>None</div>
              ) : (
                alreadyMapped.map((r) => (
                  <div
                    key={r.centerId}
                    style={{
                      padding: '8px 10px',
                      border: '1px solid #b7eb8f',
                      background: '#f6ffed',
                      borderRadius: 4,
                      marginBottom: 6,
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>
                      {r.centerName || `Center #${r.centerId}`}{' '}
                      <span style={{ color: '#9b9b9b', fontWeight: 400, fontSize: 12 }}>
                        #{r.centerId}
                      </span>
                    </div>
                    {r.locality && (
                      <div style={{ fontSize: 12, color: '#7c7c7c' }}>{r.locality}</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </Col>
          <Col span={8}>
            <div style={{ fontWeight: 600, marginBottom: 8, color: '#cf1322' }}>
              Invalid ({failed.length})
            </div>
            <div style={scrollListStyle}>
              {failed.length === 0 ? (
                <div style={{ color: '#9b9b9b', fontSize: 13 }}>None</div>
              ) : (
                failed.map((r) => (
                  <div
                    key={r.centerId}
                    style={{
                      padding: '8px 10px',
                      border: '1px solid #ffa39e',
                      background: '#fff1f0',
                      borderRadius: 4,
                      marginBottom: 6,
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>
                      {r.centerName || `Center #${r.centerId}`}{' '}
                      <span style={{ color: '#9b9b9b', fontWeight: 400, fontSize: 12 }}>
                        #{r.centerId}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: '#cf1322' }}>{r.error}</div>
                  </div>
                ))
              )}
            </div>
          </Col>
        </Row>
        {toMap.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <Button
              type="primary"
              onClick={this.openPracticeModal}
              style={{ background: PURPLE, borderColor: PURPLE }}
            >
              Map {toMap.length} center{toMap.length === 1 ? '' : 's'} to doctor
            </Button>
          </div>
        )}
      </div>
    );
  }

  renderPracticeModal() {
    const { practiceModalVisible, practice, submitting, verifyResults } = this.state;
    const toMapCount = (verifyResults || []).filter((r) => !r.isMapped && !r.error).length;
    const opType = (field) => (
      <Select
        value={practice[field]}
        onChange={(v) => this.setPractice({ [field]: v })}
        style={{ width: 110 }}
      >
        <Option value="percentage">Percentage</Option>
        <Option value="fixed">Fixed</Option>
      </Select>
    );
    return (
      <Modal
        title={`Practice details for ${toMapCount} center${toMapCount === 1 ? '' : 's'}`}
        visible={practiceModalVisible}
        onCancel={this.closePracticeModal}
        width={760}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={this.closePracticeModal} disabled={submitting}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={submitting}
            onClick={this.submit}
            style={{ background: PURPLE, borderColor: PURPLE }}
          >
            Confirm and map
          </Button>,
        ]}
      >
        <Alert
          type="info"
          showIcon
          message="These details will be applied identically to every center in the list."
          style={{ marginBottom: 16 }}
        />

        {/* Section: General Settings */}
        <div style={{ borderLeft: '4px solid #1890ff', paddingLeft: 10, marginBottom: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: '#1890ff' }}>General Settings</span>
        </div>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}>
            <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#595959', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active</div>
              <Switch checked={practice.active} disabled />
              <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 5 }}>Always active for new centers</div>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#595959', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Onboarding Status</div>
              <Select value={practice.status} disabled style={{ width: '100%' }}>
                <Option value="pending">Pending</Option>
              </Select>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#595959', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payout To</div>
              <Select value={practice.fulfilmentType} onChange={(v) => this.setPractice({ fulfilmentType: v })} style={{ width: '100%' }}>
                <Option value="center">Center</Option>
                <Option value="doctor">Doctor</Option>
              </Select>
            </div>
          </Col>
          <Col xs={12} md={6}>
            <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#595959', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Telehealth</div>
              <Switch checked={practice.telehealth} onChange={(v) => this.setPractice({ telehealth: v })} />
              <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 5 }}>Enable online video consultations</div>
            </div>
          </Col>
        </Row>

        {/* Section: Charges */}
        <div style={{ borderLeft: '4px solid #13c2c2', paddingLeft: 10, marginBottom: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: '#13c2c2' }}>Charges</span>
        </div>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={8}>
            <div style={{ background: '#e6fffb', border: '1px solid #87e8de', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#08979c', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>OPD Charges</div>
              <InputNumber value={practice.opdCharges} onChange={(v) => this.setPractice({ opdCharges: v || 0 })} style={{ width: '100%' }} min={0} />
              <div style={{ fontSize: 11, color: '#08979c', marginTop: 5 }}>Offline consultation fee</div>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ background: '#e6fffb', border: '1px solid #87e8de', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#08979c', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Online OPD Charges</div>
              <InputNumber value={practice.onlineOpdCharges} onChange={(v) => this.setPractice({ onlineOpdCharges: v || 0 })} style={{ width: '100%' }} min={0} />
              <div style={{ fontSize: 11, color: '#08979c', marginTop: 5 }}>Online consultation fee</div>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ background: '#e6fffb', border: '1px solid #87e8de', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#08979c', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Online Discount</div>
              <Input value={practice.onlineDiscount} addonBefore={opType('onlineDiscountOperation')} onChange={(e) => this.setPractice({ onlineDiscount: Number(e.target.value) || 0 })} placeholder="0" />
              <div style={{ fontSize: 11, color: '#08979c', marginTop: 5 }}>Discount on online consultations</div>
            </div>
          </Col>
        </Row>

        {/* Section: Payout Configuration — 4-box panel */}
        <div style={{ borderLeft: '4px solid #722ed1', paddingLeft: 10, marginBottom: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: '#722ed1' }}>Payout Configuration</span>
        </div>
        <div style={{ marginBottom: 16 }}>
            <div
              style={{
                background: 'linear-gradient(135deg, #f9f7ff 0%, #fffbf0 100%)',
                border: '1px solid #d3c8f5',
                borderRadius: 10,
                padding: '16px 18px 14px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, borderBottom: '1px solid #e3deff', paddingBottom: 10 }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#5a3fc0' }}>Payout Configuration</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Checkbox checked={practice.enableVisitCommission} onChange={this.onEnableVisitCommissionChange}>
                    <span style={{ fontSize: 12 }}>Set Visit Commission (default 70/30)</span>
                  </Checkbox>
                  <div
                    style={{
                      background: practice.visitCommissionPercent + practice.offlinePayoutPercent === 100 ? '#f6ffed' : '#fff1f0',
                      border: `1px solid ${practice.visitCommissionPercent + practice.offlinePayoutPercent === 100 ? '#b7eb8f' : '#ffa39e'}`,
                      borderRadius: 20, padding: '2px 12px', fontSize: 12, fontWeight: 600,
                      color: practice.visitCommissionPercent + practice.offlinePayoutPercent === 100 ? '#52c41a' : '#f5222d',
                    }}
                  >
                    {practice.visitCommissionPercent}% + {practice.offlinePayoutPercent}% = {practice.visitCommissionPercent + practice.offlinePayoutPercent}%
                  </div>
                </div>
              </div>
              <Row gutter={[12, 12]}>
                {/* Visit Payout Discount — has the dropdown */}
                <Col xs={24} md={12}>
                  <div style={{ background: '#fff', border: '1px solid #d3c8f5', borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#5a3fc0', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Visit Payout Discount</div>
                    <Input
                      value={practice.payoutOfflineDiscount}
                      addonBefore={opType('offlineDiscountOperation')}
                      onChange={(e) => {
                        const val = Number(e.target.value) || 0;
                        if (practice.offlineDiscountOperation === 'percentage' && val > 100) {
                          message.error('Percentage value cannot be greater than 100%');
                          return;
                        }
                        const newOfflineDiscount = Math.round(val * (practice.offlinePayoutPercent / 100) * 100) / 100;
                        this.setPractice({ payoutOfflineDiscount: val, offlineDiscount: newOfflineDiscount });
                      }}
                      placeholder="0"
                    />
                    <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 5 }}>Discount taken from the partner center</div>
                  </div>
                </Col>

                {/* Offline Discount — auto-calculated, disabled */}
                <Col xs={24} md={12}>
                  <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#d48806', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Offline Discount (User Discount)</div>
                    <Input
                      value={practice.offlineDiscount}
                      addonBefore={
                        <span style={{ display: 'inline-block', padding: '0 11px', color: 'rgba(0,0,0,0.65)' }}>
                          {practice.offlineDiscountOperation === 'fixed' ? 'Fixed' : 'Percentage'}
                        </span>
                      }
                      disabled
                      placeholder="0"
                    />
                    <div style={{ fontSize: 11, color: '#d48806', marginTop: 5 }}>Auto-calculated · Discount given to the patient</div>
                  </div>
                </Col>

                {/* Visit Commission % */}
                <Col xs={24} md={12}>
                  <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#389e0d', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Visit Commission %</div>
                    <Input
                      value={practice.visitCommissionPercent}
                      addonAfter="%"
                      disabled={!practice.enableVisitCommission}
                      onChange={this.onVisitCommissionPercentChange}
                      placeholder="70"
                    />
                    <div style={{ fontSize: 11, color: '#52c41a', marginTop: 5 }}>This is the Visit Commission</div>
                  </div>
                </Col>

                {/* User Discount % */}
                <Col xs={24} md={12}>
                  <div style={{ background: '#fff0f6', border: '1px solid #ffadd2', borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#c41d7f', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>User Discount %</div>
                    <Input
                      value={practice.offlinePayoutPercent}
                      addonAfter="%"
                      disabled
                      placeholder="30"
                    />
                    <div style={{ fontSize: 11, color: '#eb2f96', marginTop: 5 }}>This is the Offline / User Discount</div>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
          {/* Section: Follow-up */}
          <div style={{ borderLeft: '4px solid #fa8c16', paddingLeft: 10, marginBottom: 8, marginTop: 4 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#fa8c16' }}>Follow-up</span>
          </div>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={24} md={12}>
              <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#d46b08', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Follow-up Discount</div>
                <Input value={practice.fDiscount} addonBefore={opType('fDiscountOperation')} onChange={(e) => this.setPractice({ fDiscount: Number(e.target.value) || 0 })} placeholder="0" />
                <div style={{ fontSize: 11, color: '#d46b08', marginTop: 5 }}>Offline follow-up visit discount</div>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#d46b08', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Follow-up Days</div>
                <InputNumber value={practice.fDays} onChange={(v) => this.setPractice({ fDays: v || 0 })} style={{ width: '100%' }} min={0} placeholder="0" />
                <div style={{ fontSize: 11, color: '#d46b08', marginTop: 5 }}>Valid days for offline follow-up</div>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#d46b08', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Online Follow-up Discount</div>
                <Input value={practice.onlineFDiscount} addonBefore={opType('onlineFDiscountOperation')} onChange={(e) => this.setPractice({ onlineFDiscount: Number(e.target.value) || 0 })} placeholder="0" />
                <div style={{ fontSize: 11, color: '#d46b08', marginTop: 5 }}>Online follow-up visit discount</div>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#d46b08', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Online Follow-up Days</div>
                <InputNumber value={practice.onlineFDays} onChange={(v) => this.setPractice({ onlineFDays: v || 0 })} style={{ width: '100%' }} min={0} placeholder="0" />
                <div style={{ fontSize: 11, color: '#d46b08', marginTop: 5 }}>Valid days for online follow-up</div>
              </div>
            </Col>
          </Row>

          {/* Section: Telehealth Payout (conditional) */}
          {practice.telehealth && (
            <React.Fragment>
              <div style={{ borderLeft: '4px solid #2f54eb', paddingLeft: 10, marginBottom: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#2f54eb' }}>Telehealth Payout</span>
              </div>
              <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                <Col xs={24} md={12}>
                  <div style={{ background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#2f54eb', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Online Retail Payout</div>
                    <InputNumber value={practice.onlineRetailPayout} onChange={(v) => this.setPractice({ onlineRetailPayout: v || 0 })} style={{ width: '100%' }} min={0} />
                    <div style={{ fontSize: 11, color: '#2f54eb', marginTop: 5 }}>Retail patient payout amount</div>
                  </div>
                </Col>
                <Col xs={24} md={12}>
                  <div style={{ background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#2f54eb', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Online Corporate Payout</div>
                    <InputNumber value={practice.onlineCorporatePayout} onChange={(v) => this.setPractice({ onlineCorporatePayout: v || 0 })} style={{ width: '100%' }} min={0} />
                    <div style={{ fontSize: 11, color: '#2f54eb', marginTop: 5 }}>Corporate patient payout amount</div>
                  </div>
                </Col>
              </Row>
            </React.Fragment>
          )}

          {/* Section: Reason */}
          <div style={{ borderLeft: '4px solid #cf1322', paddingLeft: 10, marginBottom: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: '#cf1322' }}>Reason for Change <span style={{ fontSize: 12 }}>*</span></span>
          </div>
          <div style={{ background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 8, padding: '12px 14px' }}>
            <Select
              value={practice.priceChangeReason}
              onChange={(v) =>
                this.setPractice({
                  priceChangeReason: v,
                  priceChangeReasonOther: v === 'Other' ? practice.priceChangeReasonOther : '',
                })
              }
              placeholder="Select reason for this change"
              style={{ width: '100%', marginBottom: practice.priceChangeReason === 'Other' ? 8 : 0 }}
            >
              {REASON_OPTIONS.map((r) => (
                <Option key={r} value={r}>{r}</Option>
              ))}
            </Select>
            {practice.priceChangeReason === 'Other' && (
              <Input
                value={practice.priceChangeReasonOther}
                onChange={(e) => this.setPractice({ priceChangeReasonOther: e.target.value })}
                placeholder="Please describe the reason"
              />
            )}
          </div>
      </Modal>
    );
  }

  renderResultSection() {
    const { submitResult } = this.state;
    if (!submitResult) return null;
    const summary = submitResult.summary || {};
    const results = submitResult.results || [];
    return (
      <div style={sectionStyle}>
        <div style={sectionTitleStyle}>3. Result</div>
        <Alert
          type="success"
          showIcon
          message={`Added ${summary.totalAdded || 0} center(s) to doctor's practice.`}
          description={`${summary.totalSkipped || 0} skipped, ${summary.totalFailed || 0} failed.`}
          style={{ marginBottom: 12 }}
        />
        <div style={scrollListStyle}>
          {results.map((r) => (
            <div
              key={r.centerId}
              style={{
                padding: '8px 10px',
                border: '1px solid #f0f0f0',
                borderRadius: 4,
                marginBottom: 6,
                background:
                  r.status === 'added'
                    ? '#f6ffed'
                    : r.status === 'failed'
                    ? '#fff1f0'
                    : '#fffbe6',
              }}
            >
              <div style={{ fontWeight: 500 }}>
                {r.centerName || `Center #${r.centerId}`}{' '}
                <span style={{ color: '#9b9b9b', fontWeight: 400, fontSize: 12 }}>
                  #{r.centerId}
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#7c7c7c' }}>
                status: {r.status}
                {r.reason ? ` · ${r.reason}` : ''}
                {r.error ? ` · ${r.error}` : ''}
              </div>
            </div>
          ))}
        </div>
        <Button onClick={this.reset} style={{ marginTop: 8 }}>
          Start another bulk add
        </Button>
      </div>
    );
  }

  render() {
    const step = this.currentStep();
    return (
      <div>
        <div
          style={{
            background: '#ffffff',
            border: '1px solid #e7e7e7',
            borderRadius: 6,
            padding: '18px 24px',
            marginBottom: 16,
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
        >
          <Steps current={step} size="small">
            <Step title="Enter IDs" />
            <Step title="Verify" />
            <Step title="Map" />
            <Step title="Result" />
          </Steps>
        </div>
        {this.renderEnterSection()}
        {this.renderVerifySection()}
        {this.renderResultSection()}
        {this.renderPracticeModal()}
      </div>
    );
  }
}

export default DoctorPracticeBulkAdd;
